import React, { useState, useEffect } from 'react';
import { ActivityItem } from '../types';
import { activityApi } from '../api.ts';
import { formatDistanceToNow } from '../utils/dateUtils.ts';

interface ActivityFeedProps {
  onStationClick: (stationId: number) => void;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ onStationClick }) => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadActivities();

    // Auto-refresh every 30 seconds
    const interval = setInterval(loadActivities, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadActivities = async () => {
    try {
      setError(null);
      const data = await activityApi.getRecent(100);
      setActivities(data);
    } catch (error) {
      console.error('Erreur lors du chargement des activités:', error);
      setError('Impossible de charger les activités récentes');
    } finally {
      setLoading(false);
    }
  };

  const getActivityMessage = (activity: ActivityItem): string => {
    const stationName = activity.station_name;

    // Use proper French articles with fuel types
    let fuelTypeWithArticle;
    if (activity.fuel_type === 'essence') {
      fuelTypeWithArticle = "de l'essence";
    } else if (activity.fuel_type === 'gasoil' || activity.fuel_type === 'diesel') {
      fuelTypeWithArticle = "du gasoil";
    } else {
      fuelTypeWithArticle = "du carburant";
    }

    if (activity.status === 'available') {
      return `${stationName} a ${fuelTypeWithArticle} disponible`;
    } else if (activity.status === 'low') {
      return `${stationName} a un stock faible ${fuelTypeWithArticle}`;
    } else {
      return `${stationName} est en rupture ${fuelTypeWithArticle}`;
    }
  };

  const getActivityIcon = (status: string): string => {
    switch (status) {
      case 'available': return '✅';
      case 'low': return '⚠️';
      case 'out': return '❌';
      default: return '❓';
    }
  };

  const formatActivityTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const timeAgo = formatDistanceToNow(timestamp);

    return `${hours}:${minutes} • ${timeAgo}`;
  };

  if (loading && activities.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Chargement des activités...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-red-600">
          <span className="text-2xl">🔄</span>
          <p className="mt-2 font-medium">Problème de connexion</p>
          <p className="text-sm text-gray-600 mt-1">{error}</p>
          <button
            onClick={loadActivities}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-600">
          <span className="text-4xl">📡</span>
          <p className="mt-2 font-medium">Aucune activité récente</p>
          <p className="text-sm mt-1">Les derniers signalements apparaîtront ici</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            🔴 Temps réel
          </h3>
          <button
            onClick={loadActivities}
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
            title="Actualiser"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Activités récentes des stations de carburant
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activities.map((activity) => (
          <div
            key={activity.id}
            className="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => onStationClick(activity.station_id)}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <span className="text-lg">{getActivityIcon(activity.status)}</span>
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {getActivityMessage(activity)}
                </p>

                {activity.comment && (
                  <p className="text-xs text-gray-600 mt-1 italic border-l-2 border-gray-200 pl-2">
                    "{activity.comment}"
                  </p>
                )}

                <p className="text-xs text-gray-500 mt-1">
                  {formatActivityTime(activity.created_at)}
                  {activity.price && (
                    <span className="ml-2 text-green-600 font-medium">
                      {activity.price} FCFA/L
                    </span>
                  )}
                </p>
              </div>

              <div className="flex-shrink-0">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityFeed;